import { AudioEngine } from './audio-engine.js';
import { StateManager } from './state-manager.js';
import { PlaylistManager } from './playlist-manager.js';
import { UIController } from './ui-controller.js';

/**
 * PodcastPlayer - Main orchestrator for the podcast player
 * Coordinates all modules and provides high-level API
 */
class PodcastPlayer {
  constructor() {
    this.audioEngine = new AudioEngine();
    this.stateManager = new StateManager();
    this.playlistManager = null;
    this.uiController = new UIController();

    this.state = {
      currentEpisodeUrl: null,
      currentTime: 0,
      isPlaying: false,
      playbackRate: 1.0,
      volume: 1.0,
      currentIndex: -1,
      isDismissed: false
    };

    this.isInitialized = false;
  }

  /**
   * Initialize the player
   */
  init() {
    if (this.isInitialized) return;

    // Initialize UI controller
    this.uiController.init();

    // Initialize playlist from window.podcastData
    if (window.podcastData && window.podcastData.episodes) {
      this.playlistManager = new PlaylistManager(window.podcastData.episodes);
    } else {
      console.warn('No podcast data found');
      this.playlistManager = new PlaylistManager([]);
    }

    // Set up audio engine callbacks
    this.setupAudioCallbacks();

    // Bind UI event listeners
    this.bindEventListeners();

    // Restore state from localStorage
    this.restoreState();

    // Handle timestamp in URL hash
    this.handleTimestampHash();

    // Save state before page unload
    window.addEventListener('beforeunload', () => {
      this.saveState(true);
    });

    this.isInitialized = true;
  }

  /**
   * Set up audio engine event callbacks
   */
  setupAudioCallbacks() {
    this.audioEngine.onplay = () => {
      this.state.isPlaying = true;
      this.uiController.updatePlayButton(true);
      this.stateManager.autoSave(this.state);
    };

    this.audioEngine.onpause = () => {
      this.state.isPlaying = false;
      this.uiController.updatePlayButton(false);
      this.saveState();
    };

    this.audioEngine.onend = () => {
      this.state.isPlaying = false;
      this.uiController.updatePlayButton(false);

      // Reset to beginning when audio ends
      this.state.currentTime = 0;
      this.audioEngine.seek(0);
      this.uiController.updateProgress(0, this.audioEngine.getDuration());

      this.saveState();

      // Auto-play next episode if available
      if (this.playlistManager.hasNext()) {
        setTimeout(() => {
          this.nextEpisode();
        }, 1000);
      }
    };

    this.audioEngine.ontimeupdate = (currentTime, duration) => {
      this.state.currentTime = currentTime;
      this.uiController.updateProgress(currentTime, duration);

      // Auto-save every 5 seconds during playback
      if (this.state.isPlaying) {
        this.stateManager.autoSave(this.state, 5000);
      }
    };

    this.audioEngine.onload = () => {
      this.uiController.hideLoading();
      const duration = this.audioEngine.getDuration();
      this.uiController.updateProgress(0, duration);
    };

    this.audioEngine.onerror = (error) => {
      this.uiController.hideLoading();
      this.uiController.showError('Failed to load audio');
    };
  }

  /**
   * Bind UI event listeners
   */
  bindEventListeners() {
    // Play/pause button
    const playButton = this.uiController.getPlayButton();
    if (playButton) {
      playButton.addEventListener('click', () => this.togglePlay());
    }

    // Skip buttons
    const skipForward = document.getElementById('player-skip-forward');
    if (skipForward) {
      skipForward.addEventListener('click', () => this.skipForward());
    }

    const skipBackward = document.getElementById('player-skip-backward');
    if (skipBackward) {
      skipBackward.addEventListener('click', () => this.skipBackward());
    }

    // Next/previous buttons
    const nextButton = document.getElementById('player-next-btn');
    if (nextButton) {
      nextButton.addEventListener('click', () => this.nextEpisode());
    }

    const prevButton = document.getElementById('player-prev-btn');
    if (prevButton) {
      prevButton.addEventListener('click', () => this.previousEpisode());
    }

    // Speed toggle button
    const speedToggle = this.uiController.getSpeedToggle();
    if (speedToggle) {
      speedToggle.addEventListener('click', () => {
        this.cycleSpeed();
      });
    }

    // Progress bar
    const progressBar = this.uiController.getProgressBar();
    if (progressBar) {
      progressBar.addEventListener('input', (e) => {
        const duration = this.audioEngine.getDuration();
        const time = (e.target.value / 100) * duration;
        this.seek(time);
      });
    }

    // Close button
    const closeButton = document.getElementById('player-close-btn');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.pause();
        this.uiController.hidePlayer();
        // Mark as dismissed so player doesn't reappear on page navigation
        this.state.isDismissed = true;
        this.saveState(true); // Save immediately
      });
    }

    // Play episode buttons (on listing and detail pages)
    document.addEventListener('click', (e) => {
      const playBtn = e.target.closest('.play-episode-btn');
      if (playBtn) {
        e.preventDefault();
        const url = playBtn.dataset.episodeUrl;
        const title = playBtn.dataset.episodeTitle;
        const slug = playBtn.dataset.episodeSlug;
        const date = playBtn.dataset.episodeDate;

        if (url) {
          this.loadEpisodeByUrl(url, true);
        }
      }
    });

    // Keyboard shortcuts
    this.setupKeyboardShortcuts();
  }

  /**
   * Set up keyboard shortcuts
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.matches('input, textarea')) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          this.togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.skipBackward();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.skipForward();
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.increaseSpeed();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.decreaseSpeed();
          break;
      }
    });
  }

  /**
   * Load episode by URL
   * @param {string} url - Episode URL
   * @param {boolean} autoplay - Whether to auto-play
   */
  loadEpisodeByUrl(url, autoplay = true) {
    if (!url) return;

    this.uiController.showLoading();

    // Find episode in playlist
    const found = this.playlistManager.setCurrentByUrl(url);
    if (found) {
      const episode = this.playlistManager.getCurrentEpisode();
      this.loadEpisode(episode, autoplay);
    } else {
      // Episode not in playlist, load it anyway
      const episode = { url, title: 'Unknown Episode' };
      this.loadEpisode(episode, autoplay);
    }
  }

  /**
   * Load an episode
   * @param {Object} episode - Episode object
   * @param {boolean} autoplay - Whether to auto-play
   */
  loadEpisode(episode, autoplay = true) {
    if (!episode || !episode.url) return;

    this.state.currentEpisodeUrl = episode.url;
    this.state.currentIndex = this.playlistManager.getCurrentIndex();
    // Clear dismissed flag when loading a new episode
    this.state.isDismissed = false;

    // Prepare MediaSession metadata
    const metadata = {
      title: episode.title || 'Unknown Episode',
      artist: 'Hippocratic Society',
      album: 'Hippocratic Society Podcast',
      artwork: episode.artwork ? [
        { src: episode.artwork, sizes: '512x512', type: 'image/jpeg' }
      ] : []
    };

    // Load audio with metadata
    this.audioEngine.load(episode.url, autoplay, metadata);

    // Update UI
    this.uiController.showPlayer();
    this.uiController.updateEpisodeInfo(episode);
    this.uiController.updateNavigationButtons(
      this.playlistManager.hasNext(),
      this.playlistManager.hasPrevious()
    );

    // Apply saved playback rate
    this.audioEngine.setRate(this.state.playbackRate);
    this.uiController.updateSpeedButton(this.state.playbackRate);

    this.saveState();
  }

  /**
   * Play audio
   */
  play() {
    this.audioEngine.play();
  }

  /**
   * Pause audio
   */
  pause() {
    this.audioEngine.pause();
  }

  /**
   * Toggle play/pause
   */
  togglePlay() {
    if (this.audioEngine.isPlaying()) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Seek to specific time
   * @param {number} time - Time in seconds
   */
  seek(time) {
    this.audioEngine.seek(time);
    this.state.currentTime = time;

    // Update UI immediately (important when paused)
    const duration = this.audioEngine.getDuration();
    this.uiController.updateProgress(time, duration);
  }

  /**
   * Play from specific timestamp (used by chapters/transcripts)
   * Shows player if dismissed, then seeks and plays
   * @param {number} time - Time in seconds
   * @param {string} episodeUrl - Optional episode URL to load first
   */
  playFromTimestamp(time, episodeUrl = null) {
    // If episodeUrl is provided and different from current, load it first
    if (episodeUrl && episodeUrl !== this.state.currentEpisodeUrl) {
      this.loadEpisodeByUrl(episodeUrl, false);
      // Wait for audio to load before seeking
      setTimeout(() => {
        this.seek(time);
        this.play();
      }, 500);
      return;
    }

    // If no episode is loaded, try to load from page's play button
    if (!this.state.currentEpisodeUrl) {
      const playBtn = document.querySelector('.play-episode-btn');
      if (playBtn) {
        const url = playBtn.dataset.episodeUrl;
        if (url) {
          this.loadEpisodeByUrl(url, false);
          // Wait for audio to load before seeking
          setTimeout(() => {
            this.seek(time);
            this.play();
          }, 500);
          return;
        }
      }
      // No episode to play
      console.warn('No episode loaded and could not find episode on page');
      return;
    }

    // If player is dismissed, un-dismiss it
    if (this.state.isDismissed) {
      this.state.isDismissed = false;
      this.uiController.showPlayer();
      this.saveState(true);
    }

    // Seek to the timestamp and play
    this.seek(time);
    this.play();
  }

  /**
   * Skip forward 15 seconds
   */
  skipForward() {
    const currentTime = this.audioEngine.getCurrentTime();
    const newTime = Math.min(currentTime + 15, this.audioEngine.getDuration());
    this.seek(newTime);
  }

  /**
   * Skip backward 15 seconds
   */
  skipBackward() {
    const currentTime = this.audioEngine.getCurrentTime();
    const newTime = Math.max(currentTime - 15, 0);
    this.seek(newTime);
  }

  /**
   * Next episode
   */
  nextEpisode() {
    const nextEpisode = this.playlistManager.moveNext();
    if (nextEpisode) {
      this.loadEpisode(nextEpisode, true);
    }
  }

  /**
   * Previous episode
   */
  previousEpisode() {
    const prevEpisode = this.playlistManager.movePrevious();
    if (prevEpisode) {
      this.loadEpisode(prevEpisode, true);
    }
  }

  /**
   * Set playback speed
   * @param {number} rate - Playback rate (0.5 to 2.0)
   */
  setPlaybackSpeed(rate) {
    this.state.playbackRate = rate;
    this.audioEngine.setRate(rate);
    this.uiController.updateSpeedButton(rate);
    this.saveState();
  }

  /**
   * Increase playback speed
   */
  increaseSpeed() {
    const speeds = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = speeds.indexOf(this.state.playbackRate);
    if (currentIndex < speeds.length - 1) {
      this.setPlaybackSpeed(speeds[currentIndex + 1]);
    }
  }

  /**
   * Decrease playback speed
   */
  decreaseSpeed() {
    const speeds = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = speeds.indexOf(this.state.playbackRate);
    if (currentIndex > 0) {
      this.setPlaybackSpeed(speeds[currentIndex - 1]);
    }
  }

  /**
   * Cycle through playback speeds
   * Cycles: 1x → 1.25x → 1.5x → 1.75x → 2x → 1x
   */
  cycleSpeed() {
    const speeds = [1, 1.25, 1.5, 1.75, 2];
    const currentIndex = speeds.indexOf(this.state.playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    this.setPlaybackSpeed(speeds[nextIndex]);
  }

  /**
   * Save current state
   * @param {boolean} immediate - Save immediately without debounce
   */
  saveState(immediate = false) {
    this.state.currentTime = this.audioEngine.getCurrentTime();
    this.state.isPlaying = this.audioEngine.isPlaying();

    if (immediate) {
      this.stateManager.saveImmediate(this.state);
    } else {
      this.stateManager.save(this.state);
    }
  }

  /**
   * Restore state from localStorage
   */
  restoreState() {
    const savedState = this.stateManager.load();
    if (!savedState || !savedState.currentEpisodeUrl) return;

    // Don't restore player if it was dismissed by the user
    if (savedState.isDismissed) {
      this.state.isDismissed = true;
      return;
    }

    // Restore playback rate
    if (savedState.playbackRate) {
      this.state.playbackRate = savedState.playbackRate;
    }

    // Find and load the saved episode
    const found = this.playlistManager.setCurrentByUrl(savedState.currentEpisodeUrl);
    if (found) {
      const episode = this.playlistManager.getCurrentEpisode();
      this.loadEpisode(episode, false);

      // Restore playback position
      if (savedState.currentTime) {
        setTimeout(() => {
          this.seek(savedState.currentTime);
        }, 500);
      }
    }
  }

  /**
   * Handle timestamp in URL hash (#t=123)
   */
  handleTimestampHash() {
    const hash = window.location.hash;
    if (hash.startsWith('#t=')) {
      const time = parseInt(hash.substring(3));
      if (!isNaN(time) && time >= 0) {
        setTimeout(() => {
          if (this.state.currentEpisodeUrl) {
            this.seek(time);
            this.play();
          }
        }, 1000);
      }
    }
  }
}

// Initialize player when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.podcastPlayer = new PodcastPlayer();
    window.podcastPlayer.init();
  });
} else {
  window.podcastPlayer = new PodcastPlayer();
  window.podcastPlayer.init();
}

export default PodcastPlayer;
